var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import * as UnlinkedApplicationForm from "../../../components/UnlinkedApplicationForm";
import { Button, DateTextField, PhoneTextField, TextField, } from "@savvyinsure/react-ui";
import { validateDOBFormat, validateDOBYears, validatePhone, validateUnlinkedPolicyHolderData, } from "../validateUnlinkedPolicyHolderData";
import AppBody from "../../../components/AppBody";
import { EMAIL } from "../../../utils/regexLibrary";
import { EXPERIENCE } from "../../../constants";
import Header from "../../../components/Header";
import RouteHeaderLogo from "../../../components/RouteHeaderLogo";
import { completedPage } from "../../../lib/Avo";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { savePolicyHolder } from "../unlinkedApplicationSlice";
import useAffiliateConfig from "../../../hooks/useAffiliateConfig";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { useForm } from "react-hook-form";
dayjs.extend(customParseFormat);
var ctaText = "GET QUOTE";
function TCPAConsent() {
    var isOrganicUser = useAppSelector(function (state) { return !state.configuration.utmSource; });
    return (React.createElement("label", { htmlFor: "leadid_tcpa_disclosure", className: "mb-3 block text-xs text-gray-100" },
        React.createElement("input", { type: "hidden", id: "leadid_tcpa_disclosure", name: "leadid_tcpa_disclosure" }),
        !isOrganicUser ? (React.createElement(React.Fragment, null,
            "By clicking \u201C",
            ctaText,
            "\u201D I am providing my E-Sign signature and prior express written consent to receive marketing communications about insurance products from",
            " ",
            React.createElement("strong", null, "Savvy Insurance Agency, LLC"),
            ",",
            " ",
            React.createElement("a", { href: "https://savvy.insure/business-partners", target: "_blank", rel: "noreferrer", className: "text-savvy underline" }, "its business partners"),
            ", and parties on their behalf at the number and/or email provided above via automatic telephone dialing system, SMS/MMS, AI generative voice, and artificial and/or prerecorded voice messages, even if my number is on a federal, state, or company Do-Not-Call list. I acknowledge my consent is not required to obtain any good or service and that the phone number field above is optional. Message frequency varies. Message and data rates may apply. Reply ",
            React.createElement("strong", null, "HELP"),
            " ",
            "for help and ",
            React.createElement("strong", null, "STOP"),
            " to opt out.")) : (React.createElement(React.Fragment, null,
            "By clicking \u201C",
            ctaText,
            "\u201D I am providing my E-Sign signature and prior express written consent to receive marketing communications about insurance products",
            " ",
            React.createElement("strong", null, "only from Savvy Insurance Agency, LLC"),
            ", at the number and/or email address provided above, via automatic telephone dialing system, SMS/MMS, AI generative voice, and artificial or prerecorded voice messages, even if my number is on a federal, state, or company Do-Not-Call list. I acknowledge my consent is not required to obtain any good or service and that the phone number field above is optional. Information collected from SMS will not be shared, bought, rented, or exchanged for purposes other than the insurance comparison services you have requested. Message frequency varies. Message and data rates may apply. Reply ",
            React.createElement("strong", null, "HELP"),
            " for help and",
            " ",
            React.createElement("strong", null, "STOP"),
            " to opt out."))));
}
function PrivacyPolicyConsent() {
    var affiliateConfig = useAffiliateConfig();
    return (React.createElement("p", { className: "text-xs text-gray-100" },
        React.createElement("strong", null, "Comparing insurance with Savvy is free and will not affect your credit score."),
        " ",
        "By clicking \u201C",
        ctaText,
        "\u201D I consent and agree to Savvy's",
        " ",
        React.createElement("a", { className: "text-current underline", href: affiliateConfig.privacyPolicyURL, target: "_blank", rel: "noreferrer" }, "Privacy Policy"),
        " ",
        "and",
        " ",
        React.createElement("a", { className: "text-current underline", href: "https://savvy.insure/terms", target: "_blank", rel: "noreferrer" }, "Terms of Service"),
        ", including the arbitration provision and class action waiver, and to the use of third-party consumer reports by Savvy's insurers to calculate accurate quotes for me."));
}
export default function UnlinkedApplicationPolicyHolder(_a) {
    var _b, _c, _d, _e, _f, _g;
    var disableSubmitButton = _a.disableSubmitButton, onSubmit = _a.onSubmit, handleInputsFocus = _a.handleInputsFocus;
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var policyHolder = useAppSelector(function (state) { return state.unlinkedApplication.policyHolder; });
    var isPrefill = useAppSelector(function (state) { return state.configuration.experience === EXPERIENCE.PREFILL; });
    var _h = useForm({
        mode: "all",
        defaultValues: {
            firstName: policyHolder.firstName,
            lastName: policyHolder.lastName,
            phone: policyHolder.phone,
            email: policyHolder.email,
            dateOfBirth: policyHolder.dateOfBirth,
        },
    }), register = _h.register, errors = _h.formState.errors, handleSubmit = _h.handleSubmit, watch = _h.watch;
    var dispatch = useAppDispatch();
    var firstNameInput = register("firstName", {
        required: "Please enter your first name",
    });
    var lastNameInput = register("lastName", {
        required: "Please enter your last name",
    });
    var phoneInput = register("phone", {
        validate: {
            isValid: function (value) {
                return validatePhone(value, { required: false }) ||
                    "Please enter a valid US phone number";
            },
        },
    });
    var emailInput = register("email", {
        required: "Please enter a valid email address",
        pattern: EMAIL,
    });
    var dateInput = register("dateOfBirth", {
        required: "Please enter a valid date in the format MM/DD/YYYY",
        validate: {
            isValid: function (value) {
                return validateDOBFormat(value) ||
                    "Please enter a valid date in the format MM/DD/YYYY";
            },
            isAtleastEighteen: function (value) {
                return validateDOBYears(value) ||
                    "You must be 18 or older to be the primary policy holder";
            },
        },
    });
    var _j = watch([
        "firstName",
        "lastName",
        "email",
        "phone",
        "dateOfBirth",
    ]), firstName = _j[0], lastName = _j[1], email = _j[2], phone = _j[3], dateOfBirth = _j[4];
    var submitHandler = handleSubmit(function (data) {
        dispatch(savePolicyHolder(data));
        completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: "Submit", optionSelected: "Submit" }));
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
    });
    function handleFocus() {
        handleInputsFocus === null || handleInputsFocus === void 0 ? void 0 : handleInputsFocus();
    }
    function handleOnCloseHeader() {
        var policyHolderData = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            phone: phone,
            dateOfBirth: dateOfBirth,
        };
        var validPolicyHolder = validateUnlinkedPolicyHolderData(policyHolderData);
        if (validPolicyHolder) {
            dispatch(savePolicyHolder(policyHolderData));
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { onClose: handleOnCloseHeader, showDropShadow: true },
            React.createElement(RouteHeaderLogo, null)),
        React.createElement(AppBody, { as: "form", action: "#", onSubmit: submitHandler },
            React.createElement(UnlinkedApplicationForm.ScrollingContainer, null,
                React.createElement("div", { className: "mb-4 flex-none pt-4" },
                    React.createElement(UnlinkedApplicationForm.Header, null, "Where should we send your offers?")),
                React.createElement("div", { className: "mb-6 space-y-3 md:space-y-4" },
                    policyHolder.firstName && !isPrefill ? null : (React.createElement(TextField, __assign({ id: "firstName", label: "First Name", onFocus: handleFocus, "aria-invalid": Boolean(errors.firstName), errorMessage: (_b = errors.firstName) === null || _b === void 0 ? void 0 : _b.message }, firstNameInput))),
                    policyHolder.lastName && !isPrefill ? null : (React.createElement(TextField, __assign({ id: "lastName", label: "Last Name", onFocus: handleFocus, "aria-invalid": Boolean(errors.lastName), errorMessage: (_c = errors.lastName) === null || _c === void 0 ? void 0 : _c.message }, lastNameInput))),
                    React.createElement("div", null,
                        React.createElement(PhoneTextField, { id: "phone", label: "Mobile Number", type: "tel", format: "(###) ###-####", defaultValue: (_d = policyHolder.phone) !== null && _d !== void 0 ? _d : "", onFocus: handleFocus, mask: "", onBlur: phoneInput.onBlur, onChange: phoneInput.onChange, name: phoneInput.name, getInputRef: phoneInput.ref, "aria-invalid": Boolean(errors.phone), errorMessage: (_e = errors.phone) === null || _e === void 0 ? void 0 : _e.message })),
                    React.createElement(TextField, __assign({ id: "email", label: "E-mail Address", onFocus: handleFocus, "aria-invalid": Boolean(errors.email), errorMessage: (_f = errors.email) === null || _f === void 0 ? void 0 : _f.message }, emailInput)),
                    policyHolder.dateOfBirth && !isPrefill ? null : (React.createElement(DateTextField, { id: "dateOfBirth", label: "Date of Birth (MM/DD/YYYY)", type: "text", onFocus: handleFocus, mask: "", format: "##/##/####", defaultValue: policyHolder.dateOfBirth, onBlur: dateInput.onBlur, onChange: dateInput.onChange, name: dateInput.name, getInputRef: dateInput.ref, "aria-invalid": Boolean(errors === null || errors === void 0 ? void 0 : errors.dateOfBirth), errorMessage: (_g = errors.dateOfBirth) === null || _g === void 0 ? void 0 : _g.message })),
                    React.createElement("div", { className: "block pb-2" },
                        React.createElement(PrivacyPolicyConsent, null),
                        React.createElement("div", { className: "my-3 md:my-4" },
                            React.createElement(Button, { disabled: disableSubmitButton, "data-cy": "consent-continue" }, disableSubmitButton ? "Processing..." : ctaText)),
                        React.createElement(TCPAConsent, null)))))));
}
